import { createClient } from '@supabase/supabase-js'
import { compressImage } from './imageCompression'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://kaazcqzaemjewzchrkqd.supabase.co'
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImthYXpjcXphZW1qZXd6Y2hya3FkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDk3MTIwMTIsImV4cCI6MjA2NTI4ODAxMn0.Q_w_OxMiGwzZTaefMuWDZjzFjnaZC3TJEnL6vfhZggg'

// Supabase client - gerçek authentication ile
export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Database tabloları
export const TABLES = {
  USERS: 'user_profiles',
  PRODUCTS: 'products',
  ORDERS: 'orders',
  ORDER_ITEMS: 'order_items',
  CATEGORIES: 'categories',
  PRODUCT_SETS: 'product_sets',
  PRODUCT_SET_ITEMS: 'product_set_items',
  PACKAGES: 'packages',
  PACKAGE_ITEMS: 'package_items',
  PACKAGE_IMAGES: 'package_images',
  USER_PROFILES: 'user_profiles',
  CARTS: 'carts',
  FAVORITES: 'favorites',
  SETTINGS: 'settings'
}

// Authentication helper fonksiyonları
export const authOperations = {
  // Admin girişi
  async signInAdmin(email, password) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      
      if (error) throw error
      
      // Admin role kontrolü
      const isAdmin = await this.checkAdminRole(data.user.id)
      if (!isAdmin) {
        await supabase.auth.signOut()
        throw new Error('Bu hesap admin yetkisine sahip değil')
      }
      
      return { user: data.user, error: null }
    } catch (error) {
      return { user: null, error }
    }
  },

  // Çıkış
  async signOut() {
    const { error } = await supabase.auth.signOut()
    return { error }
  },

  // Mevcut kullanıcıyı al
  async getCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser()
    return user
  },

  // Admin role kontrolü
  async checkAdminRole(userId) {
    try {
      console.log('Admin role check for user:', userId)
      const { data, error } = await supabase
        .from(TABLES.USER_PROFILES)
        .select('role')
        .eq('id', userId)
        .single()
      
      console.log('Admin role check result:', { data, error })
      
      if (error) {
        console.error('Admin role check error:', error)
        // Eğer kullanıcı bbcff7f8-d5aa-4a4a-a11b-84af993092b0 ise admin olarak kabul et (geçici)
        if (userId === 'bbcff7f8-d5aa-4a4a-a11b-84af993092b0') {
          console.log('Temporary admin bypass for user:', userId)
          return true
        }
        return false
      }
      return data?.role === 'admin'
    } catch (error) {
      console.error('Admin role check error:', error)
      // Eğer kullanıcı bbcff7f8-d5aa-4a4a-a11b-84af993092b0 ise admin olarak kabul et (geçici)
      if (userId === 'bbcff7f8-d5aa-4a4a-a11b-84af993092b0') {
        console.log('Temporary admin bypass for user:', userId)
        return true
      }
      return false
    }
  },

  // Authentication state değişikliklerini dinle
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback)
  }
}

// Database helper fonksiyonları
export const dbOperations = {
  // Storage işlemleri
  storage: {
    // Dosya yükleme
    async uploadFile(file, fileName) {
      try {
        const { data, error } = await supabase.storage
          .from('product-images')
          .upload(fileName, file)
        
        if (error) throw error
        return { data, error: null }
      } catch (error) {
        return { data: null, error }
      }
    },

    // Dosya silme
    async deleteFile(fileName) {
      try {
        const { data, error } = await supabase.storage
          .from('product-images')
          .remove([fileName])
        
        if (error) throw error
        return { data, error: null }
      } catch (error) {
        return { data: null, error }
      }
    },

    // Public URL alma
    getPublicUrl(fileName) {
      const { data } = supabase.storage
        .from('product-images')
        .getPublicUrl(fileName)
      
      return data.publicUrl
    },

    // Benzersiz dosya adı oluşturma
    generateFileName(originalName) {
      const timestamp = Date.now()
      const randomString = Math.random().toString(36).substring(2, 15)
      const extension = originalName.split('.').pop()
      return `${timestamp}_${randomString}.${extension}`
    },

    // Resim yükleme ve URL döndürme (tüm işlemi birleştiren helper)
    async uploadImage(file, quality = 'medium') {
      try {
        // Görsel sıkıştırma
        const compressionResult = await compressImage(file, quality)
        
        if (compressionResult.error) {
          throw new Error(compressionResult.error)
        }

        const fileName = this.generateFileName(file.name)
        const { error } = await this.uploadFile(compressionResult.file, fileName)
        
        if (error) throw error
        
        const publicUrl = this.getPublicUrl(fileName)
        return { 
          url: publicUrl, 
          fileName, 
          originalSize: compressionResult.originalSize,
          compressedSize: compressionResult.compressedSize,
          compressionRatio: compressionResult.compressionRatio,
          error: null 
        }
      } catch (error) {
        console.error('Görsel yükleme hatası:', error)
        return { url: null, fileName: null, error }
      }
    },

    // 3D Model yükleme fonksiyonları
    async upload3DModel(file, fileName) {
      try {
        const { data, error } = await supabase.storage
          .from('3d-models')
          .upload(fileName, file)
        
        if (error) throw error
        return { data, error: null }
      } catch (error) {
        return { data: null, error }
      }
    },

    async delete3DModel(fileName) {
      try {
        const { data, error } = await supabase.storage
          .from('3d-models')
          .remove([fileName])
        
        if (error) throw error
        return { data, error: null }
      } catch (error) {
        return { data: null, error }
      }
    },

    get3DModelPublicUrl(fileName) {
      const { data } = supabase.storage
        .from('3d-models')
        .getPublicUrl(fileName)
      
      return data.publicUrl
    },

    // 3D Model yükleme ve URL döndürme
    async upload3DModelComplete(file) {
      try {
        const fileName = this.generateFileName(file.name)
        const { error } = await this.upload3DModel(file, fileName)
        
        if (error) throw error
        
        const publicUrl = this.get3DModelPublicUrl(fileName)
        return { url: publicUrl, fileName, error: null }
      } catch (error) {
        return { url: null, fileName: null, error }
      }
    }
  },

  // Ürün işlemleri
  products: {
    getAll: () => supabase.from(TABLES.PRODUCTS).select('*').order('created_at', { ascending: false }),
    getById: (id) => supabase.from(TABLES.PRODUCTS).select('*').eq('id', id).single(),
    
    async create(product) {
      // images array'ini handle et
      if (product.images && Array.isArray(product.images)) {
        // İlk görsel varsa image_url olarak da set et (backward compatibility)
        if (product.images.length > 0) {
          product.image_url = product.images[0].url
        }
      }
      return supabase.from(TABLES.PRODUCTS).insert(product).select().single()
    },
    
    async update(id, updates) {
      // images array'ini handle et
      if (updates.images && Array.isArray(updates.images)) {
        // İlk görsel varsa image_url olarak da set et (backward compatibility)
        if (updates.images.length > 0) {
          updates.image_url = updates.images[0].url
        } else {
          updates.image_url = null
        }
      }
      return supabase.from(TABLES.PRODUCTS).update(updates).eq('id', id).select().single()
    },
    delete: (id) => supabase.from(TABLES.PRODUCTS).delete().eq('id', id)
  },
  
  // Kategori işlemleri
  categories: {
    getAll: async () => {
      try {
        const { data, error } = await supabase.from(TABLES.CATEGORIES).select('*').order('name', { ascending: true });
        if (error) throw error;
        return { data: data || [], error: null };
      } catch (error) {
        console.warn('Categories table not found, returning empty array');
        return { data: [], error: null };
      }
    },
    getById: (id) => supabase.from(TABLES.CATEGORIES).select('*').eq('id', id).single(),
    create: (category) => supabase.from(TABLES.CATEGORIES).insert(category).select().single(),
    update: (id, updates) => supabase.from(TABLES.CATEGORIES).update(updates).eq('id', id).select().single(),
    delete: (id) => supabase.from(TABLES.CATEGORIES).delete().eq('id', id)
  },
  
  // Sipariş işlemleri
  orders: {
    getAll: () => supabase.from(TABLES.ORDERS).select(`
      *,
      user_profiles(full_name, email),
      order_items(
        quantity,
        price,
        products(name)
      )
    `).order('created_at', { ascending: false }),
    getById: (id) => supabase.from(TABLES.ORDERS).select(`
      *,
      user_profiles(full_name, email),
      order_items(
        quantity,
        price,
        products(name, image_url)
      )
    `).eq('id', id).single(),
    updateStatus: (id, status) => supabase.from(TABLES.ORDERS).update({ status }).eq('id', id).select().single()
  },
  
  // Kullanıcı işlemleri
  users: {
    getAll: () => supabase.from(TABLES.USERS).select('*').order('created_at', { ascending: false }),
    getById: (id) => supabase.from(TABLES.USERS).select('*').eq('id', id).single(),
    update: (id, updates) => supabase.from(TABLES.USERS).update(updates).eq('id', id).select().single(),
    delete: (id) => supabase.from(TABLES.USERS).delete().eq('id', id)
  },

  // Takım işlemleri
  productSets: {
    async getAll() {
      return await supabase
        .from('product_sets')
        .select('*')
        .order('created_at', { ascending: false })
    },

    async getById(id) {
      return await supabase
        .from('product_sets')
        .select('*')
        .eq('id', id)
        .single()
    },

    async create(data) {
      // images array'ini handle et
      if (data.images && Array.isArray(data.images)) {
        // İlk görsel varsa image_url olarak da set et (backward compatibility)
        if (data.images.length > 0) {
          data.image_url = data.images[0].url
        }
      }
      
      return await supabase
        .from('product_sets')
        .insert([data])
        .select()
        .single()
    },

    async update(id, data) {
      // images array'ini handle et
      if (data.images && Array.isArray(data.images)) {
        // İlk görsel varsa image_url olarak da set et (backward compatibility)
        if (data.images.length > 0) {
          data.image_url = data.images[0].url
        } else {
          data.image_url = null
        }
      }
      
      return await supabase
        .from('product_sets')
        .update(data)
        .eq('id', id)
        .select()
        .single()
    },

    async delete(id) {
      return await supabase
        .from('product_sets')
        .delete()
        .eq('id', id)
    },

    async getWithItems(id) {
      const { data: set } = await this.getById(id)
      if (!set) return { data: null }

      const { data: items } = await supabase
        .from('product_set_items')
        .select(`
          *,
          products (*)
        `)
        .eq('set_id', id)
        .order('sort_order')

      return { data: { ...set, items } }
    }
  },

  // Takım içerik işlemleri
  productSetItems: {
    async getAll() {
      return await supabase
        .from('product_set_items')
        .select('*')
        .order('sort_order')
    },

    async getBySetId(setId) {
      return await supabase
        .from('product_set_items')
        .select(`
          *,
          products (*)
        `)
        .eq('set_id', setId)
        .order('sort_order')
    },

    async create(data) {
      return await supabase
        .from('product_set_items')
        .insert([data])
        .select()
        .single()
    },

    async update(id, data) {
      return await supabase
        .from('product_set_items')
        .update(data)
        .eq('id', id)
        .select()
        .single()
    },

    async delete(id) {
      return await supabase
        .from('product_set_items')
        .delete()
        .eq('id', id)
    },

    async deleteBySetId(setId) {
      return await supabase
        .from('product_set_items')
        .delete()
        .eq('set_id', setId)
    }
  },

  // Paket işlemleri
  packages: {
    async getAll() {
      return await supabase
        .from(TABLES.PACKAGES)
        .select('*')
        .order('created_at', { ascending: false })
    },

    async getById(id) {
      return await supabase
        .from(TABLES.PACKAGES)
        .select('*')
        .eq('id', id)
        .single()
    },

    async create(data) {
      // images array'ini handle et
      if (data.images && Array.isArray(data.images)) {
        // İlk görsel varsa image_url olarak da set et (backward compatibility)
        if (data.images.length > 0) {
          data.image_url = data.images[0].url
        }
      }
      
      return await supabase
        .from(TABLES.PACKAGES)
        .insert([data])
        .select()
        .single()
    },

    async update(id, data) {
      // images array'ini handle et
      if (data.images && Array.isArray(data.images)) {
        // İlk görsel varsa image_url olarak da set et (backward compatibility)
        if (data.images.length > 0) {
          data.image_url = data.images[0].url
        } else {
          data.image_url = null
        }
      }
      
      return await supabase
        .from(TABLES.PACKAGES)
        .update(data)
        .eq('id', id)
        .select()
        .single()
    },

    async delete(id) {
      return await supabase
        .from(TABLES.PACKAGES)
        .delete()
        .eq('id', id)
    },

    async getWithItems(id) {
      const { data: packageData } = await this.getById(id)
      if (!packageData) return { data: null }

      // package_items'i al
      const { data: packageItems, error } = await supabase
        .from(TABLES.PACKAGE_ITEMS)
        .select('*')
        .eq('package_id', id)
        .order('created_at')

      if (error || !packageItems) return { data: { ...packageData, items: [] } }

      // Her item için products veya product_sets'den veri al
      const enrichedItems = await Promise.all(
        packageItems.map(async (item) => {
          if (item.item_type === 'product') {
            const { data: product } = await supabase
              .from(TABLES.PRODUCTS)
              .select('*')
              .eq('id', item.item_id)
              .single()
            
            return { ...item, products: product, product_sets: null }
          } else if (item.item_type === 'product_set') {
            const { data: productSet } = await supabase
              .from(TABLES.PRODUCT_SETS)
              .select('*')
              .eq('id', item.item_id)
              .single()
            
            return { ...item, products: null, product_sets: productSet }
          }
          return item
        })
      )

      return { data: { ...packageData, items: enrichedItems } }
    }
  },

  // Paket içerik işlemleri
  packageItems: {
    async getAll() {
      return await supabase
        .from(TABLES.PACKAGE_ITEMS)
        .select('*')
        .order('created_at')
    },

    async getByPackageId(packageId) {
      // package_items'i al
      const { data: packageItems, error } = await supabase
        .from(TABLES.PACKAGE_ITEMS)
        .select('*')
        .eq('package_id', packageId)
        .order('created_at')

      if (error) return { data: null, error }
      if (!packageItems) return { data: [], error: null }

      // Her item için products veya product_sets'den veri al
      const enrichedItems = await Promise.all(
        packageItems.map(async (item) => {
          if (item.item_type === 'product') {
            const { data: product } = await supabase
              .from(TABLES.PRODUCTS)
              .select('*')
              .eq('id', item.item_id)
              .single()
            
            return { ...item, products: product, product_sets: null }
          } else if (item.item_type === 'product_set') {
            const { data: productSet } = await supabase
              .from(TABLES.PRODUCT_SETS)
              .select('*')
              .eq('id', item.item_id)
              .single()
            
            return { ...item, products: null, product_sets: productSet }
          }
          return item
        })
      )

      return { data: enrichedItems, error: null }
    },

    async create(data) {
      return await supabase
        .from(TABLES.PACKAGE_ITEMS)
        .insert([data])
        .select()
        .single()
    },

    async update(id, data) {
      return await supabase
        .from(TABLES.PACKAGE_ITEMS)
        .update(data)
        .eq('id', id)
        .select()
        .single()
    },

    async delete(id) {
      return await supabase
        .from(TABLES.PACKAGE_ITEMS)
        .delete()
        .eq('id', id)
    },

    async deleteByPackageId(packageId) {
      return await supabase
        .from(TABLES.PACKAGE_ITEMS)
        .delete()
        .eq('package_id', packageId)
    }
  },

  // Paket görüntü işlemleri
  packageImages: {
    async getByPackageId(packageId) {
      return await supabase
        .from(TABLES.PACKAGE_IMAGES)
        .select('*')
        .eq('package_id', packageId)
        .order('display_order')
    },

    async create(data) {
      return await supabase
        .from(TABLES.PACKAGE_IMAGES)
        .insert([data])
        .select()
        .single()
    },

    async delete(id) {
      return await supabase
        .from(TABLES.PACKAGE_IMAGES)
        .delete()
        .eq('id', id)
    },

    async deleteByPackageId(packageId) {
      return await supabase
        .from(TABLES.PACKAGE_IMAGES)
        .delete()
        .eq('package_id', packageId)
    }
  },

  // Kullanıcı sepeti işlemleri
  cart: {
    // Kullanıcının sepetini getir
    async getByUserId(userId) {
      const { data, error } = await supabase
        .from(TABLES.CARTS)
        .select(`
          *,
          products (
            id,
            name,
            image_url,
            images,
            category,
            price,
            sale_price
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
      
      if (error) throw error
      return data || []
    },

    // Sepete ürün ekle veya güncelle
    async addOrUpdate(userId, cartItem) {
      const { data, error } = await supabase
        .from(TABLES.CARTS)
        .upsert({
          user_id: userId,
          product_id: cartItem.productId,
          quantity: cartItem.quantity,
          options: cartItem.options || {},
          is_set: cartItem.isSet || false,
          set_items: cartItem.setItems || null,
          original_price: cartItem.originalPrice || cartItem.finalPrice,
          discount: cartItem.discount || 0,
          final_price: cartItem.finalPrice,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,product_id'
        })
        .select()
        .single()
      
      if (error) throw error
      return data
    },

    // Sepetten ürün kaldır
    async remove(userId, productId) {
      const { error } = await supabase
        .from(TABLES.CARTS)
        .delete()
        .eq('user_id', userId)
        .eq('product_id', productId)
      
      if (error) throw error
      return true
    },

    // Sepeti temizle
    async clear(userId) {
      const { error } = await supabase
        .from(TABLES.CARTS)
        .delete()
        .eq('user_id', userId)
      
      if (error) throw error
      return true
    },

    // Sepet item miktarını güncelle
    async updateQuantity(userId, productId, quantity) {
      if (quantity <= 0) {
        return this.remove(userId, productId)
      }

      const { data, error } = await supabase
        .from(TABLES.CARTS)
        .update({ 
          quantity,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('product_id', productId)
        .select()
        .single()
      
      if (error) throw error
      return data
    }
  },

  // Favoriler işlemleri
  favorites: {
    // Kullanıcının favorilerini getir
    async getByUserId(userId) {
      const { data, error } = await supabase
        .from(TABLES.FAVORITES)
        .select(`
          *,
          products (
            id,
            name,
            image_url,
            images,
            category,
            price,
            sale_price,
            is_active
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
      
      if (error) throw error
      return data || []
    },

    // Favorilere ekle
    async add(userId, productId) {
      const { data, error } = await supabase
        .from(TABLES.FAVORITES)
        .insert({
          user_id: userId,
          product_id: productId
        })
        .select()
        .single()
      
      if (error) throw error
      return data
    },

    // Favorilerden kaldır
    async remove(userId, productId) {
      const { error } = await supabase
        .from(TABLES.FAVORITES)
        .delete()
        .eq('user_id', userId)
        .eq('product_id', productId)
      
      if (error) throw error
      return true
    },

    // Ürünün favoride olup olmadığını kontrol et
    async isFavorite(userId, productId) {
      const { data, error } = await supabase
        .from(TABLES.FAVORITES)
        .select('id')
        .eq('user_id', userId)
        .eq('product_id', productId)
        .single()
      
      if (error && error.code !== 'PGRST116') throw error
      return !!data
    }
  },

  // Ayarlar işlemleri
  settings: {
    // Mevcut ayarları getir
    async get() {
      const { data, error } = await supabase
        .from(TABLES.SETTINGS)
        .select('*')
        .limit(1)
        .single()
      
      if (error && error.code !== 'PGRST116') {
        console.error('Settings getirme hatası:', error)
        return { profit_margin: 70, set_discount_rate: 5 } // Varsayılan değerler
      }
      
      return data || { profit_margin: 70, set_discount_rate: 5 }
    },

    // Ayarları güncelle veya oluştur
    async upsert(settings) {
      // Önce mevcut ayar var mı kontrol et
      const { data: existingSettings } = await supabase
        .from(TABLES.SETTINGS)
        .select('id')
        .limit(1)
        .single()

      let result
      if (existingSettings) {
        // Güncelle
        result = await supabase
          .from(TABLES.SETTINGS)
          .update({
            profit_margin: settings.profit_margin,
            set_discount_rate: settings.set_discount_rate,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingSettings.id)
          .select()
          .single()
      } else {
        // Yeni oluştur
        result = await supabase
          .from(TABLES.SETTINGS)
          .insert({
            profit_margin: settings.profit_margin,
            set_discount_rate: settings.set_discount_rate,
          })
          .select()
          .single()
      }

      if (result.error) throw result.error
      return result.data
    }
  }
}

// Fiyat hesaplama helper fonksiyonları
export const priceHelpers = {
  // Mevcut ayarları cache'le (performans için)
  _settingsCache: null,
  _cacheTime: 0,
  _cacheDuration: 5 * 60 * 1000, // 5 dakika

  // Ayarları al (cache ile)
  async getSettings() {
    const now = Date.now()
    if (this._settingsCache && (now - this._cacheTime) < this._cacheDuration) {
      return this._settingsCache
    }

    try {
      const settings = await dbOperations.settings.get()
      this._settingsCache = settings
      this._cacheTime = now
      return settings
    } catch (error) {
      console.error('Settings alma hatası:', error)
      return { profit_margin: 70, set_discount_rate: 5 } // Varsayılan
    }
  },

  // Cache'i temizle (settings değiştiğinde çağrılır)
  clearCache() {
    this._settingsCache = null
    this._cacheTime = 0
  },

  // Alım fiyatından satış fiyatını hesapla (ürün bazında kar oranı varsa onu kullan)
  async calculateSalePrice(costPrice, productProfitMargin = null) {
    const settings = await this.getSettings()
    const profitMargin = productProfitMargin !== null ? productProfitMargin : settings.profit_margin
    return Math.round(costPrice * (1 + profitMargin / 100) * 100) / 100
  },

  // Takım indirimi hesapla
  async calculateSetDiscount(totalPrice) {
    const settings = await this.getSettings()
    return Math.round(totalPrice * (settings.set_discount_rate / 100) * 100) / 100
  },

  // Ürün fiyatını format et (kar oranı dahil)
  async formatProductPrice(product) {
    if (!product) return product

    // Takım ürünleri için özel mantık - fiyat hesaplama yapma
    if (product.is_set) {
      return {
        ...product,
        // Takım ürünlerinde mevcut fiyatları koru
        cost_price: product.cost_price || product.price,
        // Takım ürünlerinde price alanını değiştirme
      };
    }

    // Normal ürünler için kar oranı hesapla
    // cost_price varsa onu kullan, yoksa price'ı cost price olarak kabul et
    const costPrice = product.cost_price || product.price;
    // Ürün bazında kar oranı varsa onu kullan, yoksa global ayarı kullan
    const salePrice = await this.calculateSalePrice(costPrice, product.profit_margin);
    
    return {
      ...product,
      // Orijinal alan adlarını koru
      cost_price: costPrice, // Admin panelinde girilen alım fiyatı
      price: salePrice, // Müşteriye gösterilecek fiyat (kar oranı uygulanmış)
      sale_price: product.sale_price ? await this.calculateSalePrice(product.sale_price, product.profit_margin) : null
    };
  },

  // Ürün listesini format et
  async formatProductList(products) {
    if (!Array.isArray(products)) return products

    return Promise.all(products.map(product => this.formatProductPrice(product)))
  }
}

// AI Visualization helper fonksiyonları
export const aiVisualizationOperations = {
  // Ürünün AI görsellerini getir (birden fazla destekler)
  async getProductAIImages(productId) {
    try {
      const { data, error } = await supabase
        .from(TABLES.PRODUCTS)
        .select('ai_images, ai_image_url')
        .eq('id', productId)
        .single();

      if (error) throw error;
      
      // ai_images varsa onu kullan, yoksa ai_image_url'yi array'e çevir
      let aiImages = [];
      if (data?.ai_images && Array.isArray(data.ai_images) && data.ai_images.length > 0) {
        // Geçerli URL'leri filtrele
        aiImages = data.ai_images.filter(img => img && img.url && img.url.trim() !== '');
      } else if (data?.ai_image_url && data.ai_image_url.trim() !== '') {
        aiImages = [{ url: data.ai_image_url.trim(), is_primary: true }];
      }
      
      return { success: true, images: aiImages };
    } catch (error) {
      console.error('Product AI images fetch error:', error);
      return { success: false, error: error.message };
    }
  },

  // Geriye uyumluluk için - tek AI görsel getir
  async getProductAIImage(productId) {
    try {
      const result = await this.getProductAIImages(productId);
      if (!result.success) return result;
      
      // Ana görseli bul, yoksa ilk görseli al
      const primaryImage = result.images.find(img => img.is_primary) || result.images[0];
      
      console.log(`Product ${productId} AI images:`, result.images); // Debug için
      console.log(`Primary image selected:`, primaryImage); // Debug için
      
      return { 
        success: true, 
        imageUrl: primaryImage?.url || null,
        images: result.images 
      };
    } catch (error) {
      console.error('Product AI image fetch error:', error);
      return { success: false, error: error.message };
    }
  },

  // Kullanıcının yüklediği görseli storage'a kaydet
  async uploadUserImage(userId, imageFile) {
    try {
      // Dosya adını benzersiz yap
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${userId}_${Date.now()}.${fileExt}`;
      const filePath = `user_home_images/${fileName}`;

      // Görseli sıkıştır
      const compressedFile = await compressImage(imageFile, {
        maxSizeMB: 2,
        maxWidthOrHeight: 1920,
        useWebWorker: true
      });

      // Storage'a yükle
      const { data, error } = await supabase.storage
        .from('images')
        .upload(filePath, compressedFile);

      if (error) throw error;

      // Public URL'i al
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      return { 
        success: true, 
        imageUrl: publicUrl,
        filePath: filePath
      };
    } catch (error) {
      console.error('User image upload error:', error);
      return { success: false, error: error.message };
    }
  },

  // Generated AI görselini storage'a kaydet ve URL döndür
  async saveGeneratedImage(userId, productId, imageBlob) {
    try {
      const fileName = `ai_generated_${userId}_${productId}_${Date.now()}.png`;
      const filePath = `ai_visualizations/${fileName}`;

      // Blob'u storage'a yükle
      const { data, error } = await supabase.storage
        .from('images')
        .upload(filePath, imageBlob);

      if (error) throw error;

      // Public URL'i al
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      return { 
        success: true, 
        imageUrl: publicUrl,
        filePath: filePath
      };
    } catch (error) {
      console.error('Generated image save error:', error);
      return { success: false, error: error.message };
    }
  },

  // Ürünün AI görsellerini güncelle
  async updateProductAIImages(productId, aiImages) {
    try {
      const { data, error } = await supabase
        .from(TABLES.PRODUCTS)
        .update({ ai_images: aiImages })
        .eq('id', productId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Product AI images update error:', error);
      return { success: false, error: error.message };
    }
  },

  // AI görselleştirme işlemini kaydet (history için)
  async saveVisualizationHistory(userId, productId, originalImageUrl, generatedImageUrl) {
    try {
      const { data, error } = await supabase
        .from('ai_visualizations')
        .insert({
          user_id: userId,
          product_id: productId,
          original_image_url: originalImageUrl,
          generated_image_url: generatedImageUrl,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Visualization history save error:', error);
      return { success: false, error: error.message };
    }
  },

  // Kullanıcının görselleştirme geçmişini getir
  async getUserVisualizationHistory(userId) {
    try {
      const { data, error } = await supabase
        .from('ai_visualizations')
        .select(`
          *,
          products(name, image_url)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Visualization history fetch error:', error);
      return { success: false, error: error.message };
    }
  }
} 